import { ofType, combineEpics } from 'redux-observable';
import {
   catchError,
   filter,
   ignoreElements,
   map,
   mapTo,
   switchMap,
   tap,
} from 'rxjs/operators';
import { from, merge, of } from 'rxjs';
import { ajax } from 'rxjs/ajax';
import { API, Auth } from 'aws-amplify';
import jwtDecode from 'jwt-decode';

import {
   CLEAR_ACTIVE_EXERCISE,
   CLEAR_ACTIVE_INTERFACE,
   CLEAR_TUTORIAL,
   forceReduce,
   initActiveExercise,
   initTutorial,
   INIT_ACTIVE_EXERCISE,
   setActiveInterface,
   clearActiveInterface,
   SET_ACTIVE_EXERCISE,
   SET_ACTIVE_INTERFACE,
   SET_TUTORIAL,
   SET_USER_DATA,
   initialSyncResponse,
   setAssetsPermissionsFromSessionStorage,
   UPDATE_SUBSCRIPTION,
   SET_COURSE,
   CLEAR_COURSE,
   initCourse,
   LOAD_SELECTED_LOOP_KITS,
   setSelectedLoopKits,
   loadSelectedLoopKits,
} from './actions';
import { removeReducer, replaceReducer } from './reducerManager';
import {
   KEYBOARD_INTERFACE_TYPE,
   SEQUENCER_INTERFACE_TYPE,
} from '../interfaces/interfaceTypes';
import { keyboardReducer } from '../interfaces/keyboard/reducer';
import { epicMiddleware } from './epicMiddleware';
import { keyboardEpics } from '../interfaces/keyboard/epics';
import { sequencerReducer } from '../interfaces/sequencer/reducer';
import { sequencerEpics } from '../interfaces/sequencer/epics';
import {
   EQ_EXERCISE_TYPE,
   INTERVAL_EXERCISE_TYPE,
   MIXER_EXERCISE_TYPE,
   TRANSCRIPTION_EXERCISE_TYPE,
} from '../exercises/exerciseTypes';
import { intervalExerciseEpics } from '../exercises/interval/epics';
import { exerciseReducer } from '../exercises/reducer';
import { tutorialReducer } from '../tutorial/reducer';
import { tutorialEpics } from '../tutorial/epics';
import { assetsUrl, authStatuses } from '../util/constants';
// import { courseReducer } from '../courses/reducer';
// import { courseEpics } from '../courses/epics';
import { transcriptionExerciseEpics } from '../exercises/transcription/epics';
import { getSubscriptionsFromState } from '../util/getSubscriptionsFromState';
import { mixerExerciseEpics } from '../exercises/mixer/epics';
import { eqExerciseEpics } from '../exercises/eq/epics';
import { defaultSelectedLoopKits } from './defaultSelectedLoopKits';

const getInterfaceReducer = (interfaceType) => {
   switch (interfaceType) {
      case KEYBOARD_INTERFACE_TYPE:
         return keyboardReducer;
      case SEQUENCER_INTERFACE_TYPE:
         return sequencerReducer;
      default:
         return null;
   }
};

const getInterfaceEpics = (interfaceType) => {
   switch (interfaceType) {
      case KEYBOARD_INTERFACE_TYPE:
         return keyboardEpics;
      case SEQUENCER_INTERFACE_TYPE:
         return sequencerEpics;
      default:
         return null;
   }
};

const getExerciseReducer = (exerciseType) => {
   switch (exerciseType) {
      default:
         return exerciseReducer;
   }
};

const getExerciseEpics = (exerciseType) => {
   switch (exerciseType) {
      case INTERVAL_EXERCISE_TYPE:
         return intervalExerciseEpics;
      case TRANSCRIPTION_EXERCISE_TYPE:
         return transcriptionExerciseEpics;
      case MIXER_EXERCISE_TYPE:
         return mixerExerciseEpics;
      case EQ_EXERCISE_TYPE:
         return eqExerciseEpics;
      default:
         return null;
   }
};

const setActiveInterfaceEpic = (action$) =>
   action$.pipe(
      ofType(SET_ACTIVE_INTERFACE),
      tap((action) => {
         replaceReducer(
            'activeInterface',
            getInterfaceReducer(action.interfaceType)
         );
      }),
      tap((action) => {
         epicMiddleware.run(getInterfaceEpics(action.interfaceType));
      }),
      map(() => forceReduce())
   );

const clearActiveInterfaceEpic = (action$) =>
   action$.pipe(
      ofType(CLEAR_ACTIVE_INTERFACE),
      tap(() => {
         removeReducer('activeInterface');
      }),
      map(() => forceReduce())
   );

const setActiveExerciseEpic = (action$, state$) =>
   action$.pipe(
      ofType(SET_ACTIVE_EXERCISE),
      // load exercise document
      switchMap((action) => {
         const activeSubscriptions = getSubscriptionsFromState(state$.value);

         return ajax({
            url: `${assetsUrl}/${
               activeSubscriptions.exercise ? 'secure' : 'public'
            }/exercise/${action.exerciseId}${
               action.moduleId ? `/module/${action.moduleId}` : ''
            }/exerciseDocument.json`,
            headers: {
               Authorization: `Bearer ${state$.value.appData.permissions.token}`,
            },
            // withCredentials: true,
            // crossDomain: true,
         }).pipe(
            map(({ response }) => ({
               exerciseDocument: response,
               levelSelect: action.levelSelect,
               nextConfig: action.nextConfig,
            }))
         );
      }),
      tap(({ exerciseDocument }) => {
         replaceReducer(
            'activeExercise',
            getExerciseReducer(exerciseDocument.exerciseControlType)
         );
      }),
      tap(({ exerciseDocument }) => {
         epicMiddleware.run(
            getExerciseEpics(exerciseDocument.exerciseControlType)
         );
      }),
      map((exerciseData) => initActiveExercise(exerciseData))
   );

const clearActiveExerciseEpic = (action$) =>
   action$.pipe(
      ofType(CLEAR_ACTIVE_EXERCISE),
      tap(() => {
         removeReducer('activeExercise');
      }),
      map(() => clearActiveInterface())
   );

const setActiveInterfaceFromExerciseEpic = (action$) =>
   action$.pipe(
      ofType(INIT_ACTIVE_EXERCISE),
      map((action) =>
         setActiveInterface(action.exerciseDocument.userIterfaceType)
      )
   );

const setTutorialEpic = (action$) =>
   action$.pipe(
      ofType(SET_TUTORIAL),
      tap(() => {
         replaceReducer('tutorial', tutorialReducer);
      }),
      tap(() => {
         epicMiddleware.run(tutorialEpics);
      }),
      map((action) => initTutorial(action))
   );

const clearTutorialEpic = (action$) =>
   action$.pipe(
      ofType(CLEAR_TUTORIAL),
      tap(() => {
         removeReducer('tutorial');
      }),
      map(() => forceReduce())
   );

// const setCourseEpic = (action$, state$) =>
//    action$.pipe(
//       ofType(SET_COURSE),
//       switchMap((action) => {
//          const activeSubscriptions = getSubscriptionsFromState(state$.value);

//          return ajax({
//             url: `${assetsUrl}/${
//                activeSubscriptions.course ? 'secure' : 'public'
//             }/course/${action.courseId}/courseDocument.json`,
//             headers: {
//                Authorization: `Bearer ${state$.value.appData.permissions.token}`,
//             },
//             // withCredentials: true,
//             // crossDomain: true,
//          }).pipe(map(({ response }) => response));
//       }),
//       tap(() => {
//          replaceReducer('course', courseReducer);
//       }),
//       tap(() => {
//          epicMiddleware.run(courseEpics);
//       }),
//       map((courseDocument) => initCourse(courseDocument))
//    );

const clearCourseEpic = (action$) =>
   action$.pipe(
      ofType(CLEAR_COURSE),
      tap(() => {
         removeReducer('course');
      }),
      map(() => forceReduce())
   );

const syncNewUserDataEpic = (action$) =>
   action$.pipe(
      ofType(SET_USER_DATA),
      filter((action) => action.userData.status === authStatuses.authenticated),
      filter(() => sessionStorage.getItem('syncData') != null)
      // switchMap(() =>
      //    from(
      //       API.post('app', '/sync/initial', {
      //          body: JSON.parse(sessionStorage.getItem('syncData')),
      //       })
      //    )
      // ),
      // map((data) => initialSyncResponse(true, data)),

      // catchError(() => of(initialSyncResponse(false))),
      // tap(() => sessionStorage.removeItem('syncData'))
   );

const userJoinEpic = (action$) =>
   action$.pipe(
      ofType(SET_USER_DATA),
      filter(
         (action) =>
            action.userData.status === authStatuses.authenticated &&
            localStorage.getItem('eiauth-hasJoined') == null
      ),
      switchMap((action) => {
         // Affiliate partner identifier
         const affiliateId = JSON.parse(localStorage.getItem('affiliate'))?.id;

         // referred from what (specific marketing campaign, chord progression generator, blog, youtube, etc.)
         const referrer = JSON.parse(sessionStorage.getItem('referredBy'));

         const joinData = {
            // path they joined from (current path) to see what they were doing when they joined
            path: window.location.pathname,

            // original path... why they came to the site
            initialPath: JSON.parse(localStorage.getItem('initialPath'))?.path,

            email: action.userData.email,
         };
         if (affiliateId) {
            joinData.affiliateId = affiliateId;
         }
         if (referrer) {
            joinData.referrer = referrer;
         }

         // return from(
         //    API.post('app', '/visit/join', {
         //       body: joinData,
         //    })
         // );
      }),
      tap(() => localStorage.setItem('eiauth-hasJoined', true)),

      catchError(() => of({})),
      ignoreElements()
   );

const clearAuthStorageEpic = (action$) =>
   action$.pipe(
      ofType(SET_USER_DATA),
      filter(
         (action) => action.userData.status === authStatuses.unauthenticated
      ),
      tap(() => {
         Object.keys(localStorage)
            .filter((key) => key.match(/^eiauth/))
            .forEach((key) => localStorage.removeItem(key));

         Object.keys(sessionStorage)
            .filter((key) => key.match(/^eiauth/))
            .forEach((key) => sessionStorage.removeItem(key));
      }),
      ignoreElements()
   );

const userVisitEpic = (action$) =>
   action$.pipe(
      ofType(SET_USER_DATA),
      filter((action) => {
         const lastVisitTime = JSON.parse(
            localStorage.getItem('eiauth-lastVisit')
         );
         let shouldUpdateVisit = true;
         if (lastVisitTime) {
            shouldUpdateVisit =
               Date.now() > lastVisitTime + 24 * 60 * 60 * 1000;
         }
         return (
            action.userData.status === authStatuses.authenticated &&
            shouldUpdateVisit
         );
      }),
      // switchMap(() => from(API.post('app', '/visit/latest'))),
      tap(() => localStorage.setItem('eiauth-lastVisit', Date.now())),

      catchError(() => of({})),
      ignoreElements()
   );

const hasValidStoredPermissions = () => {
   const storedPermissions = JSON.parse(
      sessionStorage.getItem('eiauth-accessTokenResponse')
   );
   if (storedPermissions == null) {
      return false;
   }
   if (storedPermissions.token == null) {
      return false;
   }

   if (
      new Date(storedPermissions.expiration) <
      Date.now() + 6 * 60 * 60 * 1000
   ) {
      return false;
   }
   return true;
};

const getUserPermissionsEpic = (action$) =>
   merge(
      action$.pipe(
         ofType(SET_USER_DATA),
         filter(
            (action) =>
               action.userData.status === authStatuses.authenticated &&
               !hasValidStoredPermissions()
         ),
         switchMap(() =>
            from(
               Auth.currentSession()
                  .then((session) => session.getIdToken().getJwtToken())
                  .then((token) =>
                     API.post('app', '/access-token', {
                        headers: { Authorization: `Bearer ${token}` },
                     })
                  )
            )
         )
      ),
      action$.pipe(ofType(UPDATE_SUBSCRIPTION))
   ).pipe(
      tap(({ token }) =>
         sessionStorage.setItem(
            'eiauth-accessTokenResponse',
            JSON.stringify({ token, expiration: jwtDecode(token).exp * 1000 })
         )
      ),

      catchError((err) => {
         let errorCode;
         if (err.response?.data?.error === 'SubscriptionExpiredError') {
            errorCode = 'SubscriptionExpiredError';
         } else if (err.message === 'Network Error') {
            errorCode = 'NetworkError';
         } else {
            errorCode = 'UnexpectedError';
         }
         sessionStorage.setItem(
            'eiauth-accessTokenResponse',
            JSON.stringify({ errorCode })
         );

         return of({});
      }),
      map(() => setAssetsPermissionsFromSessionStorage())
   );

// when app is loaded but they already have an assetsPermissionsResponse in session storage
const setUserPermissionsEpic = (action$) =>
   action$.pipe(
      ofType(SET_USER_DATA),
      filter(
         (action) =>
            action.userData.status === authStatuses.authenticated &&
            hasValidStoredPermissions()
      ),
      map(() => setAssetsPermissionsFromSessionStorage())
   );

const loadSelectedLoopKitsEpic = (action$) =>
   action$.pipe(
      ofType(LOAD_SELECTED_LOOP_KITS),
      mapTo(setSelectedLoopKits(defaultSelectedLoopKits))
   );

const loadUserDataOnLoadOnLoginEpic = (action$) =>
   action$.pipe(
      ofType(SET_USER_DATA),
      filter((action) => action.userData.status === authStatuses.authenticated),
      mapTo(loadSelectedLoopKits())
   );

export const appEpics = combineEpics(
   setActiveInterfaceEpic,
   clearActiveInterfaceEpic,
   setActiveExerciseEpic,
   clearActiveExerciseEpic,
   setActiveInterfaceFromExerciseEpic,
   setTutorialEpic,
   clearTutorialEpic,
   // setCourseEpic,
   clearCourseEpic,
   // syncNewUserDataEpic,
   clearAuthStorageEpic,
   // userJoinEpic,
   // userVisitEpic,
   getUserPermissionsEpic,
   setUserPermissionsEpic,
   loadSelectedLoopKitsEpic
   // loadUserDataOnLoadOnLoginEpic
);
